/**
 * The `State` class extends the `EventEmitter` class and provides a reactive state management system.
 * It allows you to store and manage state in a type-safe manner, and emit events when the state changes.
 *
 * @template T - The type of the state object. Defaults to `IStateDefault`.
 *
 * @example
 * interface MyState {
 *   count: number;
 *   name: string;
 * }
 *
 * const initialState: Partial<MyState> = { count: 0, name: 'John' };
 * const state = new State<MyState>(initialState);
 *
 * # Get state value
 * console.log(state.get('count')); // 0
 *
 * # Set state value
 * state.setValues({ count: 1 });
 * console.log(state.get('count')); // 1
 *
 * # Use state in a React component
 * function MyComponent() {
 *   const appState = state.useState();
 *   return <div>{appState.count}</div>;
 * }
 */
import * as React from 'react';
import { EventEmitter } from 'utils/events';
import logger from './log';
import { isDebugMode } from './ui/debug';
import { isEqual } from './deepEqual';

type IStateDefault = Record<string, unknown>;

export class State<T extends object = IStateDefault> extends EventEmitter {
  protected $data: T;

  protected $initialData: T;

  get data() {
    return Object.freeze({ ...this.$data });
  }

  set data(value: T) {
    throw new Error('Cannot set data directly. Use the `set` method instead.');
  }

  protected $queue: Partial<T> = {};

  constructor(defaults: Partial<T> = {}) {
    super();
    this.$data = { ...defaults } as T;
    this.$initialData = { ...defaults } as T;
  }

  /**
   * @deprecated Use `setValues` instead
   */
  set(key: keyof T, value: T[keyof T], forceChange = false): T[keyof T] {
    if (isDebugMode()) {
      logger.warn(
        `State.set() is deprecated. Use setValues() instead to update multiple values at once. Key: ${String(key)}, Value: ${value}`
      );
    }
    const oldValue = this.$data[key];
    if (oldValue !== value || forceChange) {
      this.$data[key] = value;
      this.emit('change', [key], [value], [oldValue]);
    }
    return value;
  }

  /**
   * Get a key from the state object. If key is not provided, the entire state object is returned.
   */
  /**
   * Get a key from the state object. If key is not provided, the entire state object is returned.
   */
  get(): T;
  get<K extends keyof T>(key: K): T[K];
  get<K extends keyof T>(key?: K): T | T[K] {
    if (!key) {
      return this.$data;
    }
    return this.$data[key];
  }

  /**
   * Set multiple values in the state object
   * @param values - The values to set
   * @param options - Configuration options
   */
  /**
   * Updates multiple state values at once.
   *
   * @param values - Partial state object containing the values to update
   * @param options - Configuration options
   * @param options.reset - When true, resets state to initial values before applying new values (default: false).
   *   The single `change` emission then announces the UNION of reverted and passed keys, comparing each
   *   key's FINAL value (passed here, else its post-reset default) against its TRUE pre-reset value —
   *   so on reset emissions `changedKeys` may contain keys ABSENT from the `values` payload argument.
   *   Reset emissions carry a trailing 6th positional `meta` argument of `{ reset: true }` (non-reset
   *   emissions pass no meta), and `previousState` on `beforechange` is the pre-reset snapshot.
   * @param options.emit - When true, emits a 'change' event after values are updated (default: true).
   *   With `reset: true`, `emit: false` makes the reset fully silent: NEITHER `beforechange` NOR
   *   `change` fires (a non-reset `emit: false` still fires `beforechange`).
   *
   * @fires {Event} beforechange - Emitted before any values are changed
   * @fires {Event} change - Emitted after values have been changed (only if emit=true and values actually changed)
   *
   * @event beforechange
   * @type {Object}
   * @property {Array<keyof T>} changedKeys - Keys of values that will change
   * @property {Array<T[keyof T]>} changedValues - New values that will be set
   * @property {Array<T[keyof T]>} oldValues - Previous values being replaced
   * @property {Partial<T>} values - The complete values object passed to setValues
   * @property {T} previousState - A copy of the state before any changes
   *
   * @event change
   * @type {Object}
   * @property {Array<keyof T>} changedKeys - Keys that were changed
   * @property {Array<T[keyof T]>} changedValues - New values that were set
   * @property {Array<T[keyof T]>} oldValues - Previous values that were replaced
   * @property {Partial<T>} values - The complete values object passed to setValues
   * @property {T} currentState - The complete state after changes
   * @property {{ reset: true } | undefined} meta - Trailing argument: `{ reset: true }` on reset
   *   emissions, absent (undefined) on all other emissions
   *
   * @returns {void}
   */
  setValues(values: Partial<T>, options?: { reset?: boolean; emit?: boolean }) {
    const { reset = false, emit = true } = options || {};

    if (reset) {
      return this.$applyReset(values, emit);
    }

    const changedKeys: (keyof T)[] = [];
    const changedValues: T[keyof T][] = [];
    const oldValues: T[keyof T][] = [];

    // Compile the list of changed keys and values
    Object.keys(values).forEach((key) => {
      const value = values[key as keyof T];
      const oldValue = this.$data[key as keyof T];
      if (!isEqual(value, oldValue)) {
        changedKeys.push(key as keyof T);
        changedValues.push(value as T[keyof T]);
        oldValues.push(oldValue);
      }
    });

    if (!changedKeys.length) {
      // No changes detected, nothing to do
      return;
    }

    // Inform listeners about the changes that are going to happen
    // This is useful for components that need to prepare for the change or silently add other changes to the re-render
    // the final argument is the previous state and it has to be destructured to avoid passing a reference that will change in the next step
    this.emit('beforechange', changedKeys, changedValues, oldValues, values, { ...this.$data });

    // Apply changes
    this.$applyValues(values);

    if (emit && changedKeys.length) {
      this.emit('change', changedKeys, changedValues, oldValues, values, this.$data);
    }

    return values;
  }

  /**
   * Reset-path of setValues: revert to $initialData, apply `values`, and announce the UNION of
   * reverted and passed keys in one synchronous emission (contract documented on setValues).
   */
  private $applyReset(values: Partial<T>, emit: boolean) {
    const previous = this.$data;
    // Route through reset() so subclass overrides run on both the bare and the setValues path
    this.reset();

    // A silent reset (emit:false) suppresses BOTH beforechange and change — apply and exit
    if (!emit) {
      this.$applyValues(values);
      return values;
    }

    const changedKeys: (keyof T)[] = [];
    const changedValues: T[keyof T][] = [];
    const oldValues: T[keyof T][] = [];

    // Diff the union of pre-reset, initial, and passed keys: a key is announced when its FINAL
    // value (passed, else the post-reset default) differs from its TRUE pre-reset value
    const unionKeys = new Set([...Object.keys(previous), ...Object.keys(this.$initialData), ...Object.keys(values)]);
    unionKeys.forEach((key) => {
      const value = Object.prototype.hasOwnProperty.call(values, key)
        ? (values[key as keyof T] as T[keyof T])
        : this.$data[key as keyof T];
      const oldValue = previous[key as keyof T];
      if (!isEqual(value, oldValue)) {
        changedKeys.push(key as keyof T);
        changedValues.push(value);
        oldValues.push(oldValue);
      }
    });

    if (!changedKeys.length) {
      // Nothing to announce, but reset() already reverted $data — still apply the passed values
      // so the final state matches the diff's conclusion (identical to the pre-reset state)
      this.$applyValues(values);
      return values;
    }

    // beforechange fires AFTER the reset already reverted $data, with the TRUE pre-reset
    // previousState. The arrays and payload are the LIVE references the change emit will carry,
    // so listeners may still inject keys (mutate `values` and push into the arrays) as on the
    // non-reset path.
    this.emit('beforechange', changedKeys, changedValues, oldValues, values, { ...previous });

    // Apply from the possibly listener-mutated values
    this.$applyValues(values);

    // The trailing meta argument marks this emission as a reset so listeners can tell a revert
    // burst from an ordinary edit
    this.emit('change', changedKeys, changedValues, oldValues, values, this.$data, { reset: true });

    return values;
  }

  private $applyValues(values: Partial<T>) {
    Object.keys(values).forEach((key) => {
      const value = values[key as keyof T];
      const oldValue = this.$data[key as keyof T];
      if (!isEqual(value, oldValue)) {
        this.$data[key as keyof T] = value as T[keyof T];
      }
    });
  }

  // Queue the values to be set later
  queue(values: Partial<T>) {
    this.$queue = { ...this.$queue, ...values };
  }

  // Apply the queued values to the state
  applyQueue() {
    // Apply the queued values to the state
    this.setValues(this.$queue);
    // Clear the queue after applying
    this.$queue = {};
  }

  /**
   * @deprecated For production code — call `setValues(values, { reset: true })` instead so
   * listeners hear the revert; a bare reset() swaps $data back silently. Tests may keep using it.
   */
  reset() {
    this.$data = { ...this.$initialData };
  }

  /**
   * Make it easier to use the hook without having to import it
   */
  useState() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useAppState<T>(this);
  }
}

export function useAppState<T extends object = IStateDefault>(stateInstance: State<T>): T {
  // we work with Symbols to maintain low memory footprint. We just need to trigger re-render
  const [, setState] = React.useState(Symbol('initial'));
  React.useEffect(() => {
    // `on` returns an unsubscribe closure — return it so the listener is removed on
    // unmount. Without this, every mount permanently leaks a 'change' listener on the
    // app-lifetime singletons (userSession(), tenantConfig(), …) and re-renders can fire
    // after unmount.
    const unsubscribe = stateInstance.on('change', (changedKeys: (keyof T)[]) => {
      setState(Symbol(String(changedKeys?.[0])));
    });
    return unsubscribe;
  }, [stateInstance]);

  return stateInstance.data;
}
